/**
 * `la-mirage.in` host predicate — the single place that decides whether the
 * browser is on the venue's marketing / booking domain.
 *
 * Why this exists at all
 * ----------------------
 * `client/src/App.tsx` treats ANY hostname that is not `lamirage.co` /
 * `*.lamirage.co` / localhost / 127.0.0.1 / replit as **a couple's custom
 * wedding domain** and renders the wedding-website public page
 * (`App.tsx` `isCustomWeddingDomain`, three separate sites). `la-mirage.in`
 * matches that predicate, so without an explicit early return the venue's
 * ranking domain renders "Website Not Found" on every URL. `lamirage.pics`
 * needed exactly this treatment for exactly this reason.
 *
 * It is a shared module, and not another inline `hostname === '…'` expression,
 * because the same question is asked at more than one branch point and the
 * three existing hand-rolled copies of `isCustomWeddingDomain` are the reason
 * that predicate is now impossible to change safely.
 *
 * Matching rule
 * -------------
 * Apex **or any subdomain** — mirroring the two in-repo precedents:
 *   - `App.tsx`: `liveHost === 'lamirage.live' || liveHost.endsWith('.lamirage.live')`
 *   - the plan's Chunk 9 guard: `hostname === 'la-mirage.in' || hostname.endsWith('.la-mirage.in')`
 *
 * The subdomain arm is deliberate rather than incidental. `la-mirage.in` is
 * wholly ours — no couple ever gets a subdomain of it — so a broader match
 * costs nothing, whereas an exact two-host allowlist would leave a future
 * `staging.la-mirage.in` falling through into the wedding-website renderer,
 * which is the failure this file exists to prevent.
 *
 * ⚠️ The suffix test is anchored on a leading dot (`.la-mirage.in`), which is
 * what makes `la-mirage.in.evil.com` a non-match. Never relax it to
 * `hostname.includes('la-mirage.in')`.
 */

const LA_MIRAGE_IN = 'la-mirage.in';

/**
 * Reduce a raw host string to a bare, comparable hostname.
 *
 * Accepts either `window.location.hostname` (no port) or
 * `window.location.host` (may carry `:443`), because callers reach for
 * whichever is nearest and the difference is invisible until the one day the
 * app is served on a non-default port.
 */
function normalizeHost(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  let host = raw.trim().toLowerCase();
  if (host === '') return '';

  // Bracketed IPv6 literal (`[::1]:5000`). Never one of our domains — return it
  // untouched rather than letting the port-stripping below mangle the colons.
  if (host.startsWith('[')) return host;

  // Strip a `:port` suffix. Only when what follows the last colon is entirely
  // digits, so a malformed value is left alone instead of being truncated into
  // something that might accidentally match.
  const lastColon = host.lastIndexOf(':');
  if (lastColon !== -1 && /^\d+$/.test(host.slice(lastColon + 1))) {
    host = host.slice(0, lastColon);
  }

  // `la-mirage.in.` is the fully-qualified form of the same host. Browsers do
  // reach it, and a trailing dot is a well-known way to slip past a naive
  // equality check.
  if (host.endsWith('.')) host = host.slice(0, -1);

  return host;
}

/**
 * True when the browser is on `la-mirage.in` (or any subdomain of it).
 *
 * False for `lamirage.co`, `lamirage.live`, `lamirage.pics`, a couple's
 * wedding subdomain or custom domain, localhost, and lookalikes such as
 * `la-mirage.in.evil.com`.
 */
export function isLaMirageInHost(hostname: string): boolean {
  const host = normalizeHost(hostname);
  if (host === '') return false;
  return host === LA_MIRAGE_IN || host.endsWith(`.${LA_MIRAGE_IN}`);
}

export default isLaMirageInHost;
